import { Reveal, SectionLabel, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { B2B_CHANNELS } from "@/lib/content";

export default function Distribution() {
    return (
        <section
            id="distribution"
            className="relative border-t border-white/10"
            data-testid="distribution"
        >
            <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
                <Reveal>
                    <SectionLabel index="06">
                        B2B Distribution & White-Label Strategy
                    </SectionLabel>
                </Reveal>

                {/* Prominent framing line + opening paragraph */}
                <div className="grid gap-12 md:grid-cols-12">
                    <div className="md:col-span-7">
                        <Reveal delay={0.1}>
                            <h2 className="font-display text-3xl md:text-5xl font-light leading-[1.15] tracking-tight text-white">
                                The same product that helps an individual finally
                                start their legacy plan can also become a{" "}
                                <span className="italic text-gold">
                                    high-value tool for the professionals who serve
                                    them.
                                </span>
                            </h2>
                        </Reveal>
                    </div>
                    <div className="md:col-span-5">
                        <Reveal delay={0.2}>
                            <p className="text-base md:text-lg leading-relaxed text-white/70">
                                The Phase 1 go-to-market is a focused B2B2C
                                motion. HEIR is selling bulk licenses to a
                                handful of high-fit verticals, mid-market
                                employers (50–500), estate attorneys, insurance
                                brokers, and real estate brokers, and equipping
                                them with a fully white-labelable $88 guided
                                experience they can deliver inside existing client
                                relationships.
                            </p>
                        </Reveal>
                    </div>
                </div>

                {/* Channels grid: 4 priority verticals + 4 broader ecosystem */}
                <StaggerGroup className="mt-16 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
                    {B2B_CHANNELS.map((channel) => {
                        const Icon = channel.icon;
                        return (
                            <StaggerItem key={channel.label}>
                                <div className="group relative flex h-full items-start gap-5 bg-navy-950 p-7 transition-colors duration-300 hover:bg-navy-900">
                                    {channel.priority && (
                                        <span className="absolute right-3 top-3 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-gold">
                                            <span className="h-1 w-1 rounded-full bg-gold" />
                                            Priority
                                        </span>
                                    )}
                                    <Icon
                                        className="h-6 w-6 shrink-0 text-gold transition-transform duration-500 group-hover:scale-110"
                                        strokeWidth={1.25}
                                    />
                                    <span className="text-base font-medium leading-snug text-white">
                                        {channel.label}
                                    </span>
                                </div>
                            </StaggerItem>
                        );
                    })}
                </StaggerGroup>

                {/* Why these channels — full-width band */}
                <Reveal delay={0.15}>
                    <div className="mt-10 border border-gold/25 bg-gold/[0.04] p-8 md:p-10">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                            Why These Channels
                        </p>
                        <p className="mt-5 text-base md:text-lg leading-relaxed text-white/80">
                            These professionals already discuss protection,
                            family, and the future with their clients. Most of
                            those clients are still delaying basic estate
                            planning. A simple, branded $88 entry point lets the
                            partner deliver clear value while creating warmer
                            opportunities for their core services, from policies
                            and AUM to legal work and closing-table conversations.
                        </p>
                    </div>
                </Reveal>

                {/* White-label offering + investor payoff */}
                <div className="mt-14 grid gap-8 md:grid-cols-2">
                    <Reveal delay={0.1}>
                        <div className="h-full border border-white/10 p-8 md:p-10">
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                                The White-Label Offering
                            </p>
                            <p className="mt-6 text-base md:text-lg leading-relaxed text-white/75">
                                Partners can fully white-label or co-brand the
                                experience, receive volume pricing, and access a
                                simple partner dashboard with training and
                                reporting. The goal is to make distribution feel
                                native to their existing client relationships rather
                                than a separate product push.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="h-full border border-gold/25 bg-gold/[0.04] p-8 md:p-10">
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                                The Investor Payoff
                            </p>
                            <p className="mt-6 text-base md:text-lg leading-relaxed text-white/90">
                                This channel strategy turns the $88 product into a
                                scalable B2B motion. It supports the near-term goal
                                of reaching $2M in revenue while building a
                                distribution network that compounds in value. Each
                                partner relationship can drive repeated volume and
                                deepen HEIR&apos;s position as infrastructure for
                                legacy planning.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
